import React, { useEffect, useRef } from 'react';
import FlightBookingFlow from '../components/flights/FlightBookingFlow';
import { decodeSavedFlight } from '../utils/flightBooking';
import { bookingParamReader } from '../utils/bookingContext';

/** The context hands back real arrays; the URL fallback hands back JSON strings. Accept both. */
const asArray = (v) => {
  if (Array.isArray(v)) return v;
  try { const parsed = JSON.parse(v || '[]'); return Array.isArray(parsed) ? parsed : []; }
  catch { return []; }
};

/**
 * Popup host for the flight flow. Mirrors HotelBookingPage: the opener passes the trip context on
 * the query string, and the result comes back by postMessage so the opener owns the Firestore
 * write. Keeping the write there means one code path updates the sector, whichever flow produced
 * the result.
 */
export default function FlightBookingPage() {
  // Reads the context the opener stashed, falling back to the query string — which is what keeps
  // a link built before the hand-off existed, or one opened with storage blocked, working.
  // Built once: collecting the context deletes it, so a second reader would find nothing.
  const paramsRef = useRef(null);
  if (!paramsRef.current) paramsRef.current = bookingParamReader();
  const params = paramsRef.current;
  const mode   = params('mode') === 'quote' ? 'quote' : 'book';

  useEffect(() => {
    document.title = mode === 'quote' ? 'Flight Option — STX Connect' : 'Flight Booking — STX Connect';
  }, [mode]);

  const tripId      = params('tripId');
  const sectorIndexRaw = params('sectorIndex', null);
  const sectorIndex = sectorIndexRaw === null || sectorIndexRaw === '' ? null : Number(sectorIndexRaw);
  const clientId    = params('clientId');

  // Passenger identity travels as discrete fields rather than a display name: the flow needs the
  // legal first/last split to pre-fill the ticket name, and a display name cannot be split safely.
  const passengerProfile = {
    firstName:   params('paxFirstName'),
    lastName:    params('paxLastName'),
    middleName:  params('paxMiddleName'),
    dateOfBirth: params('paxDob'),
    gender:      params('paxGender'),
    email:       params('email'),
    phone:       params('paxPhone'),
    loyaltyPrograms:   asArray(params('paxLoyalty', '[]')),
    identityDocuments: asArray(params('paxDocs', '[]')),
  };

  // Everyone on this sector — the traveller plus any support worker or carer travelling with them.
  // Falls back to the single-passenger params above, so a link built before this existed (or a
  // saved option re-opened from an older trip) still books the traveller alone rather than nobody.
  const passengerList = (() => {
    const parsed = asArray(params('pax', '[]'));
    if (parsed.length > 0) return parsed;
    return [{
      name: params('travellerName'),
      role: 'traveller',
      ...passengerProfile,
    }];
  })();

  // Opened from the trip FORM, the result lands in unsaved form state and the booker still has to
  // press Save; opened from the trip PAGE it is written to Firestore immediately. The closing
  // message says which, so nobody walks away from a ticketed flight thinking it is recorded when
  // it is sitting in an unsaved form.
  const needsTripSave = params('openedFrom') === 'form';

  // Called from the confirmation screen, not the moment the booking confirms. Closing the window
  // on confirmation is what left people with no idea whether the payment had been approved.
  const handleBooked = (bookingData) => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'FLIGHT_BOOKED', tripId, sectorIndex, bookingData }, window.location.origin);
    }
    window.close();
  };

  const handleSaveOption = (optionData) => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'FLIGHT_OPTION_SAVED', tripId, sectorIndex, optionData }, window.location.origin);
    }
    window.close();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <FlightBookingFlow
        tripId={tripId}
        sectorIndex={sectorIndex}
        clientId={clientId}
        mode={mode}
        savedOption={decodeSavedFlight(params('savedOption'))}
        originPre={params('origin')}
        destinationPre={params('destination')}
        datePre={params('date')}
        airlinePre={params('airline')}
        ssrCodes={asArray(params('ssr', '[]'))}
        ssrOther={params('ssrOther')}
        travellerName={params('travellerName')}
        travellerId={params('travellerId')}
        email={params('email')}
        passengerProfile={passengerProfile}
        passengerList={passengerList}
        onBooked={handleBooked}
        onSaveOption={handleSaveOption}
        needsTripSave={needsTripSave}
      />
    </div>
  );
}
